import { api } from "../api/client";

/**
 * The catalogue copy, as the panel sees it (M1.13 §2). Hand-written against
 * `packages/functions/src/catalogue.ts` for the reason `shows/api.ts` gives.
 */

export interface CatalogueEntry {
  variantId: string;
  productId: string;
  productTitle: string;
  variantTitle: string | null;
  sku: string | null;
  /** Shopify's price, VAT-inclusive, as a decimal string. */
  price: string;
  compareAtPrice: string | null;
  /** `R-LOT-31` — panel only. */
  unitCost: string | null;
  /**
   * `unitCost` plus 20% VAT where Shopify charges tax on the variant, as is — the lowest bid that
   * does not lose money; the cost itself where there is no VAT, or where the row predates the VAT
   * flag. Null only where there is no cost.
   */
  breakEven: string | null;
  imageUrl: string | null;
  imageAlt: string | null;
  tracked: boolean;
  /** False where the location it sells from cannot be decided, so it cannot be featured. */
  sellable: boolean;
  /** Shopify's stock as last heard — for showing, never for deciding. Null when untracked. */
  available: number | null;
  /** When `available` was last written. */
  stockSyncedAt: string | null;
  /** In a delta only: the variant or its product has left the catalogue. */
  removed: boolean;
}

export interface SyncRun {
  kind: "full" | "stock";
  trigger: "nightly" | "manual" | "go-live" | "first-load";
  state: "running" | "succeeded" | "failed";
  products: number | null;
  variants: number | null;
  drift: number | null;
  removed: number | null;
  durationMs: number | null;
  error: string | null;
  startedAt: string;
  finishedAt: string | null;
}

/** D5 and D6, the shipping fee and the break-even addition — global, and editable from the settings page. */
export interface Settings {
  minFeatureStock: number;
  featureSharePercent: number;
  /**
   * The flat shipping fee, VAT-inclusive, as a two-place decimal string. Above zero. Copied onto a
   * show when it is published, so a change applies to shows published afterwards.
   */
  shippingFee: string;
  /**
   * Added to every break-even bid for packing and postage, as a two-place decimal string. Zero or
   * more. Already included in every `breakEven` the API sends.
   */
  breakEvenAddition: string;
  /** The Meta Pixel ID the customer application reports to (`R-ANL-8`), or empty for none. */
  metaPixelId: string;
}

export interface CatalogueAnswer {
  full: boolean;
  /** The `since` for the next delta. Null only for a catalogue that has never synced. */
  watermark: string | null;
  entries: CatalogueEntry[];
  runs: { full: SyncRun | null; stock: SyncRun | null };
  settings: Settings;
}

export const catalogue = {
  /** The whole index, or with `since`, only what changed — removals included. */
  read: (since?: string) =>
    api.get<CatalogueAnswer>(
      since ? `/api/catalogue?since=${encodeURIComponent(since)}` : "/api/catalogue",
    ),

  /** Starts a full rewrite from Shopify in the background. Answers at once. */
  resync: () => api.post<{ started: boolean }>("/api/catalogue/sync"),

  /**
   * Syncs one product now, from its ID, its gid or its link in the Shopify admin. Answers with the
   * product's variants as the copy now holds them.
   */
  syncProduct: (product: string) =>
    api.post<{
      productId: string;
      /** In the copy (`written`, `unchanged`), out of it (`removed`), or unknown to Shopify (`absent`). */
      outcome: "written" | "unchanged" | "removed" | "absent";
      entries: CatalogueEntry[];
    }>("/api/catalogue/products", { product }),

  settings: () => api.get<{ settings: Settings }>("/api/settings"),

  saveSettings: (changes: Partial<Settings>) =>
    api.put<{ settings: Settings }>("/api/settings", changes),
};
